import {
  InternalLlmShimJournalEntry,
  InternalLlmShimJournalFilter,
  InternalLlmShimJournalPayload,
  LlmShimJournalId,
  Region,
} from "platformed-client/types";
import {platformedClient} from "../index";

/**
 * Cross-region list of CompleteChat shim journal entries. Fans out to each given
 * region in parallel and merges the results. Per-region failures are
 * surfaced but non-fatal: a single region being down does not block the
 * other regions from rendering.
 */
export async function listEntries(
  regions: Region[],
  args: InternalLlmShimJournalFilter,
): Promise<{entries: InternalLlmShimJournalEntry[]; errors: {region: Region; error: unknown}[]}> {
  // Drop null / undefined / empty entries so they don't get serialised as
  // `?account_id=null` and confuse the backend's nullable-bind filter.
  const query = Object.fromEntries(Object.entries(args).filter(([, v]) => v != null && v !== ""));
  const results = await Promise.allSettled(
    regions.map(region =>
      platformedClient.internalRequest("get", "/region/{region}/api/internal/llm-shim-journal", {
        path: {region},
        query,
      }),
    ),
  );

  const entries: InternalLlmShimJournalEntry[] = [];
  const errors: {region: Region; error: unknown}[] = [];
  for (const [i, result] of results.entries()) {
    if (result.status === "fulfilled") {
      entries.push(...result.value);
    } else {
      errors.push({region: regions[i], error: result.reason});
    }
  }

  entries.sort((a, b) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    if (a.llm_shim_journal_id > b.llm_shim_journal_id) return -1;
    if (a.llm_shim_journal_id < b.llm_shim_journal_id) return 1;
    return 0;
  });

  return {entries, errors};
}

/** Full payloads for one journal row, queried in the row's home region. */
export async function getPayload(
  region: Region,
  llmShimJournalId: LlmShimJournalId,
): Promise<InternalLlmShimJournalPayload> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/internal/llm-shim-journal/{llm_shim_journal_id}",
    {path: {region, llm_shim_journal_id: llmShimJournalId}},
  );
}
